import React, { useState, useEffect, useRef } from 'react';
import AIModal from './AIModal';
import { subscribeAITaskCompleted } from '../utils/aiEvents';
import {  SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { uploadFile, baseImageToOss, generateRandomCode } from '../utils/common';
import { OSS_BUCKET, OSS_BASE_URL } from '../config';

interface TextLayer {
  id: string;
  text: string; 
}

interface FrameLayer {
  id: string;
  url: string;
  ai?: string;
  options: {
    left: number;
    top: number;
    width: number;
    height: number;
    rotation: number;
    fontSize: number;
    fill: string;
    frameStyle: string;
    frameSize: number;
    zIndex: number;
  };
}

interface AIGenerateResponse {
  code: string;
  src: string;
  status: string;
}

interface ProductLayersEditorProps {
  textLayers: TextLayer[];
  frameLayers: FrameLayer[];
  onTextChange: (id: string, newContent: string) => void;
  onFrameImageChange: (id: string, newImage: string,AI:string) => void;
  onFrameAIChange?: (id: string, ai: string | undefined, url?: string) => void;
}

const ProductLayersEditor: React.FC<ProductLayersEditorProps> = ({
  textLayers,
  frameLayers,
  onTextChange,
  onFrameImageChange,
  onFrameAIChange
}) => {
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [selectedLayerForAI, setSelectedLayerForAI] = useState<string | null>(null);
  const selectedLayerForAIRef = useRef<string | null>(null);

  useEffect(() => {
    selectedLayerForAIRef.current = selectedLayerForAI;
  }, [selectedLayerForAI]);

  // 获取文件扩展名的辅助函数
  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '.jpg'; // 默认扩展名
    }
    return filename.substring(lastDotIndex);
  };


  // AI 处理监控状态（必须在组件内部声明）
  const [aiRefreshTick, setAiRefreshTick] = useState(0);

  // 订阅 AI 完成事件，触发刷新
  useEffect(() => {
    const unsubscribe = subscribeAITaskCompleted((res) => {  
      if (selectedLayerForAIRef.current && res.status=="completed") { 
        onFrameAIChange?.(selectedLayerForAIRef.current, res.code, res.url);
        setAiRefreshTick((t) => t + 1);
      }
    });
    return () => unsubscribe();
  }, []);
 

  const handleAIGenerate = (styleModel: string, description: string) => {
    // This function is called after successful AI generation
    // The actual image URL is handled in handleAIGenerated
    console.log('AI generation completed:', { styleModel, description });
  };

  const handleAIGenerated = (data: AIGenerateResponse) => {
    // Use the returned image URL from the AI API  
    if (selectedLayerForAI && data.code && data.status=="completed") {
      onFrameAIChange?.(selectedLayerForAI, data.code, '');
    } else if (selectedLayerForAI && data.code && data.status=="pending") { 
      onFrameAIChange?.(selectedLayerForAI, '', data.src);
    } 
  };
  return (
    <div>
      {(textLayers.length > 0 || frameLayers.length > 0) && (
        <h4 className="text-base font-semibold text-gray-800 mb-4">Product Layers</h4>
      )}
      <div className="space-y-3">
        {/* Text Layers */}
        {textLayers.map((textLayer) => (
          <div key={textLayer.id}>
            <h5 className="text-xs font-medium text-gray-700 mb-2">Text Layer</h5>
            <div className="space-y-2">
              {/* Text Preview */}
              <input
                type="text"
                value={textLayer.text}
                onChange={(e) => onTextChange(textLayer.id, e.target.value)}
                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter text content..."
              />
            </div>
          </div>
        ))}
        
        {/* Frame Layers */}
        {frameLayers.map((frameLayer) => (
          <div key={frameLayer.id}>
            <h5 className="text-xs font-medium text-gray-700 mb-2">Image Layer</h5>
            <div className="flex space-x-3 items-center">
              {/* Image Preview */}
              <div className="flex-shrink-0">
                {frameLayer.url ? (
                  <div className="relative">
                    <img 
                      src={frameLayer.url} 
                      alt="Frame content" 
                      className="w-12 h-12 object-cover rounded border border-gray-300"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                    <span className="text-xs text-gray-500">No image</span>
                  </div>
                )}
              </div>
              {/* Upload Button */}
              <div className="flex justify-end">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        // 将文件转换为base64
                        const reader = new FileReader();
                        reader.onload = async (event) => {
                          const base64String = event.target?.result as string;
                          
                          try {
                            // 调用 baseImageToOss 上传到OSS，使用实际文件扩展名
                            const uploadedName = await baseImageToOss(
                              generateRandomCode() + getFileExtension(file.name), 
                              base64String, 
                              OSS_BUCKET
                            );
                            
                            console.log('上传成功:', uploadedName);
                            
                            const uploadedUrl = `${OSS_BASE_URL}/${uploadedName}`;
                            // 更新 frameLayer.url，确保缩略图也能即时更新
                            onFrameImageChange(frameLayer.id, uploadedUrl, '');
                            // 强制刷新一下缩略图显示（通过更新 key 或其他方式，这里 React 的状态更新应该会自动处理，但如果父组件传递的是新的引用，子组件会自动重渲染）
                          } catch (uploadError) {
                            console.error('上传到OSS失败:', uploadError);
                            // 如果上传失败，使用本地URL作为备选
                            const imageUrl = URL.createObjectURL(file);
                            onFrameImageChange(frameLayer.id, imageUrl, '');
                          }
                        };
                        
                        reader.readAsDataURL(file);
                        
                      } catch (error) {
                        console.error('文件处理失败:', error);
                        alert('Upload failed. Please try again.');
                      }
                    }
                  }}
                  className="hidden"
                  id={`frame-upload-${frameLayer.id}`}
                />
                <label
                  htmlFor={`frame-upload-${frameLayer.id}`}
                  className="text-blue-600 hover:text-blue-800 text-xs cursor-pointer transition-colors"
                >
                  {frameLayer.url ? 'Change Image' : 'Upload Image'}
                </label>
              </div>
              
              {/* AI Button */}
              {/* AI Value Tag */}
              {frameLayer.ai && (
                <div className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded-lg mb-2">
                  <span className="text-sm text-purple-700 truncate flex-1">
                    AI: {frameLayer.ai}
                  </span>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => {
                        setSelectedLayerForAI(frameLayer.id);
                        setIsAIModalOpen(true);
                      }}
                      className="p-1 text-purple-500 hover:text-purple-700 hover:bg-purple-100 rounded transition-colors"
                      title="Edit AI"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        // 通过回调函数更新 frameLayer 的 ai 属性
                        if (onFrameAIChange) {
                          onFrameAIChange(frameLayer.id, undefined);
                        }
                      }}
                      className="p-1 text-purple-500 hover:text-purple-700 hover:bg-purple-100 rounded transition-colors"
                      title="Remove AI"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              
              {!frameLayer.ai && (<div className="flex justify-end">
                <button
                  onClick={() => {
                    setSelectedLayerForAI(frameLayer.id);
                    setIsAIModalOpen(true);
                  }}
                  className="text-purple-600 hover:text-purple-800 text-xs cursor-pointer transition-colors flex items-center space-x-1"
                >
                  <SparklesIcon className="w-3 h-3" />
                  <span>AI Generate</span>
                </button>
              </div>)}
            </div>
          </div>
        ))}
         
      </div>
      
      {/* AI Modal */}
      <AIModal
        isOpen={isAIModalOpen}
        onClose={() => setIsAIModalOpen(false)}
        selectedLayerId={selectedLayerForAI}
        selectedLayerUrl={frameLayers.find(fl => fl.id === selectedLayerForAI)?.url}
        selectedAICode={frameLayers.find(fl => fl.id === selectedLayerForAI)?.ai}
        onGenerateAI={handleAIGenerate}
        onAIGenerated={handleAIGenerated}
      />
    </div>
  );
};

export default ProductLayersEditor;
